import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface OrganizationContextValue {
  selectedOrganizationId: string;
  setSelectedOrganizationId: (organizationId: string) => void;
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

interface OrganizationProviderProps {
  children: ReactNode;
}

const SELECTED_ORGANIZATION_KEY = 'selected-organization';
const SCHEMA_VERSION = 'v1';

interface StorageSchema {
  version: string;
  organizationId: string;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const [selectedOrganizationId, setSelectedOrganizationIdState] = useState<string>(() => {
    try {
      const item = window.localStorage.getItem(SELECTED_ORGANIZATION_KEY);
      if (!item) return '';

      const parsed = JSON.parse(item) as StorageSchema;

      // Validate schema version
      if (parsed.version !== SCHEMA_VERSION) {
        console.warn(`localStorage schema version mismatch. Expected ${SCHEMA_VERSION}, got ${parsed.version}. Clearing stored data.`);
        window.localStorage.removeItem(SELECTED_ORGANIZATION_KEY);
        return '';
      }

      // Validate organizationId is a string
      if (typeof parsed.organizationId !== 'string') {
        console.warn('Invalid organizationId type in localStorage. Clearing stored data.');
        window.localStorage.removeItem(SELECTED_ORGANIZATION_KEY);
        return '';
      }

      return parsed.organizationId;
    } catch (error) {
      console.error(`Error reading localStorage key "${SELECTED_ORGANIZATION_KEY}":`, error);
      // Clear corrupted data
      window.localStorage.removeItem(SELECTED_ORGANIZATION_KEY);
      return '';
    }
  });

  const setSelectedOrganizationId = (organizationId: string) => {
    try {
      setSelectedOrganizationIdState(organizationId);

      const data: StorageSchema = {
        version: SCHEMA_VERSION,
        organizationId
      };

      window.localStorage.setItem(SELECTED_ORGANIZATION_KEY, JSON.stringify(data));
    } catch (error) {
      console.error(`Error setting localStorage key "${SELECTED_ORGANIZATION_KEY}":`, error);
    }
  };

  // Listen for storage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === SELECTED_ORGANIZATION_KEY && event.newValue !== null) {
        try {
          const parsed = JSON.parse(event.newValue) as StorageSchema;

          // Validate schema version
          if (parsed.version !== SCHEMA_VERSION) {
            console.warn(`Received invalid schema version from storage event. Ignoring.`);
            return;
          }

          // Validate organizationId
          if (typeof parsed.organizationId !== 'string') {
            console.warn('Invalid organizationId type in storage event. Ignoring.');
            return;
          }

          setSelectedOrganizationIdState(parsed.organizationId);
        } catch (error) {
          console.error('Error parsing storage event value:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange, { passive: true });
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const value: OrganizationContextValue = {
    selectedOrganizationId,
    setSelectedOrganizationId
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganizationStore = (): OrganizationContextValue => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganizationStore must be used within an OrganizationProvider');
  }
  return context;
};